import { FormattedNumber } from '@futrli/localisation';
import { UIFeatherIcon } from '@futrli/ui-components';
import classNames from 'classnames';
import { isEqual } from 'date-fns';
import React, { FC, useState } from 'react';

import { SIZES, PeriodType } from '../../../core/constants';
import { PredictionsAccountCategories } from '../../../core/types';
import { JoinedCellProps } from '../../../forecast/detail/reports/data-grid/types';
import { FormattedAmount } from './formatted-amount';
import { NumberExplained } from './number-explained/number-explained';
import { NumberExplainedProps } from './number-explained/types';

import grid from './grid.scss';
import { isPastCell } from './current-period/current-period';
import {
  matchOnboardingBudgetRoute,
  matchOnboardingScenarioRoute,
  matchOnboardingScenarioWithInvoicesRoute
} from '@futrli/shared-services';
import { useParams } from 'react-router-dom';
import {
  BUDGET_ONBOARDING_STEP,
  SCENARIO_ONBOARDING_STEP,
  SCENARIO_WITH_INVOICES_ONBOARDING_STEP
} from '../../../forecast/onboarding/const';

/* eslint-disable complexity */

export interface CellProps {
  value: string;
  index: string;
  maxDigits: number;
  hasWarning?: boolean;
  rowHeight: SIZES;
  showDecimals: boolean;
  rowHeightOverride?: SIZES;
  isDisabled?: boolean;
  isBold?: boolean | null;
  isChild?: boolean;
  isExpanded?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isLevel1?: boolean;
  isPast?: boolean;
  isPercentage?: boolean;
  isCurrentPeriod?: boolean;
  isColored?: boolean;
  strikeThrough?: boolean | null;
  forceRowSize?: SIZES | null;
  joinedCell?: JoinedCellProps;
  numberExplained?: NumberExplainedProps;
  periodType?: PeriodType;
  category?: PredictionsAccountCategories;
  periodToDateValue?: string;
  isAssumption?: boolean;
  calculatedDateToCheck?: string;
  isFY?: boolean;
  hideCellContent?: boolean;
}

type Props = CellProps;

const MIN_FRACTION_DIGITS = 2;

export const isEqualDates = (date1: string, date2: string) => {
  return isEqual(new Date(date1), new Date(date2));
};

export const Cell: FC<Props> = props => {
  const { onboardingStep } = useParams();
  const [anchorNumberExplained, setAnchorNumberExplained] = useState<HTMLDivElement | null>(null);
  const [openNumberExplained, setOpenNumberExplained] = useState<boolean>(false);
  const {
    index,
    value,
    maxDigits,
    hasWarning,
    rowHeight,
    rowHeightOverride,
    isCurrentPeriod,
    isDisabled,
    isBold,
    isChild,
    isFirst,
    isLast,
    isLevel1,
    isExpanded,
    isPast,
    strikeThrough,
    joinedCell,
    isPercentage,
    forceRowSize,
    numberExplained,
    showDecimals,
    periodType,
    category,
    periodToDateValue,
    isColored,
    isAssumption,
    calculatedDateToCheck,
    isFY,
    hideCellContent
  } = props;

  const numberValue = Number(value);
  const isNan = isNaN(numberValue);
  const cellHeight = rowHeightOverride || rowHeight;
  const idNumberExplained = index;
  const allowedNumberExplained = Boolean(numberExplained?.allowed);

  const categoryName = category ?? '';

  const classes = classNames({
    [grid.cell]: true,
    [grid.isPast]: isPast || (periodType && isPastCell(index, periodType, calculatedDateToCheck)),
    [grid.isCurrent]: isCurrentPeriod,
    [grid.isChild]: isChild,
    [grid.isBold]: isBold,
    [grid.isDisabled]: isDisabled,
    [grid.isFirst]: isFirst,
    [grid.isLast]: isLast,
    [grid.isLevel1]: isLevel1,
    [grid.isExpanded]: isExpanded,
    [grid[cellHeight]]: true,
    [grid.withChart]: [SIZES.X_LARGE, SIZES.LARGE].includes(cellHeight),
    [grid.empty]: isNan,
    [grid.updatedStrikeThrough]: strikeThrough,
    [grid.hasJoinedCell]: joinedCell,
    [grid.forceSmallContent]: Boolean(forceRowSize) && rowHeight === SIZES.SMALL,
    [grid[categoryName]]: !!category,
    [grid.positive]: isColored && numberValue > 0,
    [grid.negative]: isColored && numberValue < 0,
    [grid.smallFont]: isAssumption,
    [grid.fyBackground]: isFY
  });

  const handleMouseEnterNumberExplained = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (
      numberExplained?.data &&
      !numberExplained?.data?.isAiAssumption &&
      numberExplained?.data?.reportSection !== 'balance_sheet' &&
      !openNumberExplained
    ) {
      setAnchorNumberExplained(event.currentTarget);
      setOpenNumberExplained(true);
    }
  };

  const handleMouseOutNumberExplained = (): void => {
    if (
      numberExplained?.data &&
      !numberExplained?.data?.isAiAssumption &&
      numberExplained?.data?.reportSection !== 'balance_sheet' &&
      openNumberExplained
    ) {
      setAnchorNumberExplained(null);
      setOpenNumberExplained(false);
    }
  };

  const handleCloseNumberExplained = () => {
    setAnchorNumberExplained(null);
    setOpenNumberExplained(false);
  };

  const renderEmpty = (classes = '') => {
    return <div className={classes} style={hideCellContent ? { opacity: 0 } : {}} />;
  };

  const renderPercentage = (numberValue: number) => {
    return (
      <FormattedNumber
        key="percentageId"
        style="percent"
        minimumFractionDigits={MIN_FRACTION_DIGITS}
        value={numberValue}
      />
    );
  };

  const renderFormattedAmount = (value: string, maxDigits: number, hasWarning = false) => {
    return (
      <React.Fragment key="formattedAmountId">
        <FormattedAmount amount={parseInt(value, 10)} maxDigits={maxDigits} hideDecimals={!showDecimals} />
        {hasWarning && (
          <div className={grid.warningIcon}>
            <UIFeatherIcon iconName="alert-circle" />
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderNumberExplained = () => {
    if (!numberExplained?.data) {
      return;
    }

    return (
      <NumberExplained
        key="numberExplainedId"
        id={idNumberExplained}
        anchorEl={anchorNumberExplained}
        open={openNumberExplained}
        onClose={handleCloseNumberExplained}
        {...numberExplained.data}
        actual={Number(periodToDateValue)}
        predicted={numberValue}
      />
    );
  };

  if (
    (matchOnboardingBudgetRoute(location.pathname) && onboardingStep === BUDGET_ONBOARDING_STEP.SELECT_TYPE) ||
    (matchOnboardingScenarioRoute(location.pathname) && onboardingStep === SCENARIO_ONBOARDING_STEP.SELECT_TYPE) ||
    (matchOnboardingScenarioWithInvoicesRoute(location.pathname) &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.SELECT_TYPE)
  ) {
    return (
      <div className={classes} aria-describedby={idNumberExplained} data-testid="cell">
        <div className={grid.placeholderValue} />
      </div>
    );
  }

  if (isDisabled || isNan) {
    return renderEmpty(classes);
  }

  if (joinedCell) {
    const renderedCells = [
      isPercentage ? renderPercentage(numberValue) : renderFormattedAmount(value, maxDigits, hasWarning),
      joinedCell.isPercentage ? (
        isNaN(Number(joinedCell.value)) ? (
          <span key="emptyPercentageId" className={grid.alignEmptyPercentage}>
            {' '}
          </span>
        ) : (
          renderPercentage(Number(joinedCell.value))
        )
      ) : (
        renderFormattedAmount(joinedCell.value, joinedCell.maxDigits, hasWarning)
      )
    ];

    return (
      <div
        className={classes}
        // aria-describedby={idNumberExplained}
        data-testid="cell"
        style={hideCellContent ? { opacity: 0 } : {}}
      >
        {renderedCells.map(cell => cell)}
        {allowedNumberExplained && renderNumberExplained()}
      </div>
    );
  }

  if (isPercentage) {
    return (
      <div
        className={classes}
        // aria-describedby={idNumberExplained}
        data-testid="cell"
        style={hideCellContent ? { opacity: 0 } : {}}
      >
        {renderPercentage(numberValue)}
        {allowedNumberExplained && renderNumberExplained()}
      </div>
    );
  }

  return (
    <div
      className={classes}
      onMouseEnter={handleMouseEnterNumberExplained}
      onMouseLeave={handleMouseOutNumberExplained}
      // aria-describedby={idNumberExplained}
      data-testid="cell"
      style={hideCellContent ? { opacity: 0 } : {}}
    >
      {renderFormattedAmount(value, maxDigits, hasWarning)}
      {allowedNumberExplained && renderNumberExplained()}
    </div>
  );
};
